/**
 * Parameter Utilities for Node Editor
 *
 * Contains functions for extracting, transforming, and serializing
 * parameter values between workflow definitions and the editor UI.
 */

import type { Action, Trigger, AgentAction } from '@/types/workflowDefinition';
import type { ValueSegment, SchemaObject } from '@/types/valueSegment';
import type { ParameterInfo } from '@/types/operationParameter';
import { parseValueToSegments, serializeSegmentsToValue } from '@/services/tokenParsingService';
import { serializeParameterValue, type ModelConfiguration, DEFAULT_AGENT_SYSTEM_MESSAGE } from '@/services/workflowSerializer';
import { isObject, setNestedDefault, deleteNestedKey } from '@/utils/primitives';
import { getBuiltInManifest } from '@/data/manifests';
import {
  collectHiddenSchemaKeys,
  collectSchemaDefaults,
  isPathOrAncestorHidden,
} from '@/utils/parameters';
import { parseInputDependencies } from '@/services/operationManifestService';
import { serializeHost, applyHostSerialization } from '@/services/hostSerializer';
import {
  isSwaggerBasedOperation,
  extractSwaggerParameters,
  extractBodyParameters,
  serializeSwaggerParameters,
  buildSwaggerInputsFromDefinitions,
  isNotificationTriggerInputs,
  extractNotificationTriggerParameters,
  buildNotificationTriggerInputs,
} from './swaggerParameterUtils';
import { isManagedApiConnector, toShortManagedApiId } from '@/utils/connections';
import {
  MANAGED_MODEL_PROVIDER_ID,
  MANAGED_MODEL_REFERENCE_KEY,
} from '@/utils/connections/managedModelReference';

/**
 * Swagger operation metadata for ApiConnection serialization
 */
export interface SwaggerOperationMetadata {
  method: string;
  pathTemplate: string;
  pathEncoding?: number;
  /** Connection reference format (determines host structure) */
  connectionFormat?: 'ApiConnection' | 'ApiConnectionWebhook' | 'ServiceProvider';
  /** Connector ID (used to populate host.apiId) */
  connectorId?: string;
  /** Operation ID (used to populate host.operationId) */
  operationId?: string;
}

/**
 * Parameter value that can be a raw value or value segments for expression support
 */
export type ParameterValue = string | number | boolean | object | null | ValueSegment[];

/**
 * For Agent nodes whose `inputsLocation` declares the `inputs.parameters`
 * wrapper, these properties are intentionally written at the top level of
 * `inputs` (and mirrored under `inputs.parameters`) by the Foundry agent
 * serializer. The wrapper-relocation pass must NOT move them.
 */
const FOUNDRY_AGENT_TOP_LEVEL_KEYS = new Set<string>([
  'agentModelType',
  'foundryAgentId',
  'foundryAgentName',
  'foundryAgentVersion',
]);

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Check if action is an Agent action
 */
export function isAgentAction(action: Action | Trigger): action is AgentAction {
  return action.type === 'Agent';
}

/**
 * Check if a value is an array of value segments
 */
export function isValueSegmentArray(value: unknown): value is ValueSegment[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return (
    typeof first === 'object' &&
    first !== null &&
    'id' in first &&
    'type' in first &&
    'value' in first
  );
}

// =============================================================================
// OBJECT UTILITIES
// =============================================================================

/**
 * Deep clone an object using structuredClone or JSON serialization
 * Works for plain data objects (no functions, circular refs, etc.)
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;

  // Use structuredClone if available (handles more edge cases)
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(obj);
    } catch {
      // Fall through to JSON approach
    }
  }

  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    // Fallback to recursive deep clone if JSON serialization fails
    if (typeof obj === 'object') {
      if (Array.isArray(obj)) {
        return obj.map(item => deepClone(item)) as T;
      }
      const cloned: Record<string, unknown> = {};
      for (const key of Object.keys(obj as object)) {
        cloned[key] = deepClone((obj as Record<string, unknown>)[key]);
      }
      return cloned as T;
    }
    return obj;
  }
}

/**
 * Set a value at a nested path in an object using dot-notation
 * Example: setNestedValue(obj, 'a.b.c', 123) sets obj.a.b.c = 123
 *
 * Note: This function handles frozen objects (from Immer) by creating
 * mutable copies as needed during path traversal.
 */
export function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  if (parts.length === 0) return;

  // Navigate/create the nested structure
  let current: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    if (!(part in current) || typeof current[part] !== 'object' || current[part] === null) {
      current[part] = {};
    } else if (Object.isFrozen(current[part])) {
      // If the nested object is frozen (from Immer), create a mutable shallow copy
      current[part] = { ...(current[part] as Record<string, unknown>) };
    }
    current = current[part] as Record<string, unknown>;
  }

  // Set the final value
  const lastPart = parts[parts.length - 1]!;
  current[lastPart] = value;
}

// =============================================================================
// FLATTENING UTILITIES
// =============================================================================

/**
 * Recursively flatten a nested object into dot-notation keys
 * Example: { a: { b: 1 } } => { 'a.b': 1 }
 * Also parses expression strings (containing @) into value segments
 */
export function flattenNestedObject(
  obj: Record<string, unknown>,
  prefix: string,
  values: Record<string, ParameterValue>
): void {
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
      // Parse expressions into value segments
      const parseResult = parseValueToSegments(value);
      if (parseResult.success && parseResult.segments.length > 0) {
        values[fullKey] = parseResult.segments;
      } else {
        values[fullKey] = value;
      }
    } else if (isObject(value)) {
      // Recursively flatten nested objects
      flattenNestedObject(value, fullKey, values);
    } else {
      values[fullKey] = value as ParameterValue;
    }
  }
}

/**
 * Recursively flatten nested object values into dot-notation keys
 * e.g., { a: { b: 1 } } with prefix "x" becomes { "x.a.b": 1 }
 * Used for specific nested properties like agentModelSettings where prefix is always required
 */
export function flattenObjectValues(
  obj: Record<string, unknown>,
  prefix: string,
  target: Record<string, ParameterValue>
): void {
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = `${prefix}.${key}`;
    if (isObject(value)) {
      flattenObjectValues(value, fullKey, target);
    } else {
      target[fullKey] = value as ParameterValue;
    }
  }
}

// =============================================================================
// PARAMETER EXTRACTION (Deserialization)
// =============================================================================

/**
 * Extract a value from a non-default inputsLocation path into parameter values.
 * Handles expression parsing for strings and JSON serialization for objects/arrays.
 */
function extractInputsLocationValue(
  values: Record<string, ParameterValue>,
  key: string,
  value: unknown
): void {
  if (typeof value === 'string') {
    if (value.startsWith('@') || value.includes('@{') || value.includes('${')) {
      const parseResult = parseValueToSegments(value);
      values[key] = (parseResult.success && parseResult.segments.length > 0)
        ? parseResult.segments
        : value;
    } else if (value !== '') {
      values[key] = value;
    }
  } else if (Array.isArray(value)) {
    values[key] = JSON.stringify(value, null, 2);
  } else if (value != null) {
    values[key] = typeof value === 'object'
      ? JSON.stringify(value, null, 2)
      : value as ParameterValue;
  }
}

/**
 * Editor-owned keys on `nodeData.metadata`. Anything outside this set is
 * treated as a file/folder picker display entry (`{ [pickerValue]:
 * pickerDisplay }`) and round-tripped via the picker-display map on the
 * editor store. Add new editor-owned metadata keys here so they aren't
 * surfaced as picker displays. Mirrors LogicAppsUX, which packs the same
 * picker map alongside connector-specific keys like `apiDefinitionUrl`.
 */
export const RESERVED_METADATA_KEYS = new Set<string>([
  'position',
  'swaggerPathTemplate',
  'operationMetadataId',
  'apiDefinitionUrl',
  'apiDefinitionLocation',
]);

/**
 * Lift file-picker `{ value: display }` entries off `nodeData.metadata` so
 * the editor can render the friendly path next to the raw id.
 */
export function extractPickerDisplayValues(
  nodeData: Action | Trigger
): Record<string, string> {
  const metadata = (nodeData as { metadata?: Record<string, unknown> }).metadata;
  if (!metadata) return {};

  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(metadata)) {
    if (RESERVED_METADATA_KEYS.has(key)) continue;
    if (typeof value !== 'string') continue;
    result[key] = value;
  }
  return result;
}

/**
 * Merge the editor's picker-display map back into `metadata`, preserving
 * reserved keys already on the action. Returns `undefined` when the result
 * is empty so callers can omit `metadata` entirely.
 */
export function serializePickerDisplayValuesToMetadata(
  existingMetadata: Record<string, unknown> | undefined,
  pickerDisplayValues: Record<string, string>
): Record<string, unknown> | undefined {
  const next: Record<string, unknown> = {};

  if (existingMetadata) {
    for (const [key, value] of Object.entries(existingMetadata)) {
      if (RESERVED_METADATA_KEYS.has(key)) {
        next[key] = value;
      }
    }
  }

  for (const [value, display] of Object.entries(pickerDisplayValues)) {
    if (!value || !display) continue;
    next[value] = display;
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Extract initial parameter values from node data
 * Parses expression strings into value segments for rich editing
 * Recursively flattens nested objects to match parameter keys with dot-notation
 */
export function extractParameterValues(nodeData: Action | Trigger): Record<string, ParameterValue> {
  const values: Record<string, ParameterValue> = {};
  const nodeDataRecord = nodeData as Record<string, unknown>;

  // Generic inputsLocation handling — use manifest to determine where inputs live
  // instead of hardcoding per operation type (If, Foreach, etc.)
  const opType = nodeDataRecord.type as string | undefined;
  const manifest = opType ? getBuiltInManifest(opType) : null;
  const inputsLocation = manifest?.properties?.inputsLocation;

  if (inputsLocation && !(inputsLocation.length === 1 && inputsLocation[0] === 'inputs')) {
    if (inputsLocation.length === 1 && inputsLocation[0]) {
      // Single non-default path like ['expression'] — entire inputs value is at that key
      const key = inputsLocation[0];
      if (key in nodeDataRecord) {
        extractInputsLocationValue(values, key, nodeDataRecord[key]);
      }
    } else if (inputsLocation.length === 0) {
      // Empty path — input properties are spread at the action top level
      const inputSchema = manifest?.properties?.inputs;
      if (inputSchema && typeof inputSchema === 'object' && 'properties' in inputSchema) {
        const schemaProps = (inputSchema as { properties: Record<string, unknown> }).properties;
        for (const propName of Object.keys(schemaProps)) {
          if (propName in nodeDataRecord) {
            extractInputsLocationValue(values, propName, nodeDataRecord[propName]);
          }
        }
      }
    }
  }

  // Get swagger path template from metadata (stored during node creation)
  const metadata = nodeData.metadata as Record<string, unknown> | undefined;
  const swaggerPathTemplate = metadata?.swaggerPathTemplate as string | undefined;

  if ('inputs' in nodeData && nodeData.inputs !== undefined && nodeData.inputs !== null) {
    const rawInputs = nodeData.inputs;

    // Check if inputs is a plain object (not a string, array, or primitive)
    // Some actions have string inputs (expressions or JSON strings)
    // Uses isObject() pattern from LogicAppsUX for reliable type detection
    const isObjectInputs = isObject(rawInputs);
    const inputs = isObjectInputs ? rawInputs : {};

    // Check if this is a notification trigger (ApiConnectionNotification with fetch/subscribe)
    // These have query parameters nested inside fetch.queries and subscribe.queries
    // Note: operationId is not extracted here - it's resolved from swagger using the fetch path
    if (isNotificationTriggerInputs(inputs)) {
      const notificationParams = extractNotificationTriggerParameters(inputs);
      Object.assign(values, notificationParams);
      // Return early - notification triggers have a different structure
      return values;
    }

    // Check if this is a swagger-based operation (ApiConnection with path/queries)
    // If so, extract parameters from their respective locations
    // Pass swagger path template if available for per-parameter path editing
    // Uses operation type (not input shape) to discriminate — matches LogicAppsUX approach
    if (isSwaggerBasedOperation(inputs, opType)) {
      const swaggerParams = extractSwaggerParameters(inputs, swaggerPathTemplate);
      Object.assign(values, swaggerParams);
      // Mark this as a swagger operation for proper serialization later
      values['_isSwaggerOperation'] = true;
    }

    // Track keys processed under 'parameters' to avoid duplicate processing
    // (workflow data may have both inputs.parameters.X and inputs.X after flattening)
    const processedParameterKeys = new Set<string>();
    // Also track swagger keys to avoid duplicate processing
    // Also include notification trigger keys
    const swaggerKeys = new Set(['path', 'pathTemplate', 'method', 'queries', 'headers', 'body', 'fetch', 'subscribe']);

    Object.entries(inputs).forEach(([key, value]) => {
      // Skip keys that were already processed under 'parameters'
      if (processedParameterKeys.has(key)) {
        return;
      }

      // Skip swagger keys if we already extracted them above
      if (values['_isSwaggerOperation'] && swaggerKeys.has(key)) {
        return;
      }

      // Flatten body object for non-swagger connector actions
      // Dynamic schemas generate params like 'recipient.groupId', 'messageBody'
      //
      // Exception: when the manifest declares `body` as its own top-level input
      // property (e.g. Response.body with type: 'any'), don't flatten — the body
      // value is itself the parameter value. Stringify objects/arrays for the
      // editor; serializeParameterValue will parse JSON strings back to native
      // values on save when the declared type allows it (any/object/array).
      const manifestInputProps = manifest?.properties?.inputs?.properties as
        | Record<string, unknown>
        | undefined;
      const bodyIsManifestProperty = !!manifestInputProps && 'body' in manifestInputProps;

      if (key === 'body' && !values['_isSwaggerOperation'] && isObject(value) && !bodyIsManifestProperty) {
        flattenNestedObject(value, '', values);
        values['_rawBody'] = JSON.stringify(value, null, 2);
      } else if (key === 'body' && bodyIsManifestProperty && (isObject(value) || Array.isArray(value))) {
        // Manifest-defined body parameter — stringify object/array for the editor
        values[key] = JSON.stringify(value, null, 2);
      } else if (key === 'host') {
        // Handle connection separately
        const host = value as Record<string, unknown>;
        if (host?.connection) {
          values['_connection'] = (host.connection as Record<string, unknown>)?.referenceName as string;
        }
        if (host?.apiId) {
          values['_apiId'] = isManagedApiConnector(host.apiId as string) ? toShortManagedApiId(host.apiId as string) : host.apiId as string;
        }
        if (host?.operationId) {
          values['_operationId'] = host.operationId as string;
        }
        // Extract workflow reference (for sub-workflow / handoff tools)
        if (host?.workflow) {
          const workflow = host.workflow as Record<string, unknown>;
          if (workflow?.id) {
            values['_workflowId'] = workflow.id as string;
          }
        }
      } else if (key === 'serviceProviderConfiguration') {
        // Handle Service Provider connection metadata
        const spConfig = value as Record<string, unknown>;
        if (spConfig?.connectionName) {
          values['_connection'] = spConfig.connectionName as string;
        }
        if (spConfig?.serviceProviderId) {
          values['_apiId'] = spConfig.serviceProviderId as string;
        }
        if (spConfig?.operationId) {
          values['_operationId'] = spConfig.operationId as string;
        }
      } else if (key === 'modelConfigurations') {
        // Handle Agent multi-slot model connections
        const modelConfigs = value as Record<string, Record<string, unknown>>;
        Object.entries(modelConfigs).forEach(([slotId, slotConfig]) => {
          if (slotConfig?.referenceName) {
            values[`_modelConnection_${slotId}`] = slotConfig.referenceName as string;
          }
          if (slotConfig?.modelConfiguration) {
            values[`_modelConfig_${slotId}`] = slotConfig.modelConfiguration as ModelConfiguration;
          }
        });
      } else if (key === 'parameters') {
        // Handle parameters nested under inputs.parameters (e.g., Agent nodes)
        values['_hasParametersWrapper'] = true;

        const params = value as Record<string, unknown>;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          processedParameterKeys.add(paramKey);

          if (paramKey === 'messages') {
            // Agent messages - extract system and user messages separately
            // Format: [{ role: "System"|"User", content: "..." }, ...]
            const messages = paramValue as Array<{ role: string; content: string }>;
            if (Array.isArray(messages) && messages.length > 0) {
              // Find system message (case-insensitive)
              const systemMessage = messages.find(m => m.role?.toLowerCase() === 'system');
              values['_systemMessage'] = systemMessage?.content ?? '';
              
              // Find user message (case-insensitive)
              const userMessage = messages.find(m => m.role?.toLowerCase() === 'user');
              values['_userMessage'] = userMessage?.content ?? '';
            } else {
              values['_systemMessage'] = '';
              values['_userMessage'] = '';
            }
          } else if (paramKey === 'agentModelSettings') {
            // Flatten agentModelSettings nested properties for individual field editing
            const settings = paramValue as Record<string, Record<string, unknown>>;
            flattenObjectValues(settings, paramKey, values);
            
            // Extract code interpreter enabled state as a special boolean field
            const chatSettings = settings?.agentChatCompletionSettings as Record<string, unknown> | undefined;
            const builtInTools = chatSettings?.builtInTools as string[] | undefined;
            values['_codeInterpreterEnabled'] = Array.isArray(builtInTools) && builtInTools.includes('code_interpreter');
          } else if (isObject(paramValue)) {
            flattenNestedObject(paramValue, paramKey, values);
          } else if (typeof paramValue === 'string' && (paramValue.includes('@') || paramValue.includes('${'))) {
            const parseResult = parseValueToSegments(paramValue);
            if (parseResult.success && parseResult.segments.length > 0) {
              values[paramKey] = parseResult.segments;
            } else {
              values[paramKey] = paramValue;
            }
          } else {
            values[paramKey] = paramValue as ParameterValue;
          }
        });
      } else if (key === 'modelConfiguration') {
        values[key] = value as ModelConfiguration;
      } else if (typeof value === 'string' && (value.includes('@') || value.includes('${'))) {
        const parseResult = parseValueToSegments(value);
        if (parseResult.success && parseResult.segments.length > 0) {
          values[key] = parseResult.segments;
        } else {
          values[key] = value;
        }
      } else if (isObject(value)) {
        values[key] = JSON.stringify(value, null, 2);
        // Swagger ops can put dynamic-schema body fields at the top level
        // alongside `body` (e.g. Teams `inputs.recipient`). Mirror
        // `extractBodyParameters`' both-alias output so x-ms-dynamic-* configs
        // that reference these via `body.<key>.<child>` resolve. The wrapper
        // entries (`<key>`, `body.<key>`) are dropped — `values[key]` already
        // holds the JSON-string editor representation.
        if (values['_isSwaggerOperation']) {
          const flattened = extractBodyParameters({ [key]: value });
          delete flattened[key];
          delete flattened[`body.${key}`];
          Object.assign(values, flattened);
        }
      } else {
        values[key] = value as ParameterValue;
      }
    });

    // Store entire inputs object for operations like Compose
    // Skip for actions with parameters wrapper (Agent actions) - they have structured inputs
    if (!values['_hasParametersWrapper']) {
      // rawInputs is already defined at the start of this block
      if (typeof rawInputs === 'string') {
        if (rawInputs.includes('@') || rawInputs.includes('${')) {
          // Parse expressions into value segments
          const parseResult = parseValueToSegments(rawInputs);
          if (parseResult.success && parseResult.segments.length > 0) {
            values['inputs'] = parseResult.segments;
          } else {
            values['inputs'] = rawInputs;
          }
        } else {
          // Plain string - store as-is
          values['inputs'] = rawInputs;
        }
      } else if (typeof rawInputs === 'number' || typeof rawInputs === 'boolean') {
        // Primitive values - store directly
        values['inputs'] = rawInputs;
      } else if (Array.isArray(rawInputs)) {
        // Array - stringify for editing
        values['inputs'] = JSON.stringify(rawInputs, null, 2);
      } else if (isObject(rawInputs)) {
        // Plain object - stringify for editing
        values['inputs'] = JSON.stringify(rawInputs, null, 2);
      }
    }
  }

  // Handle Agent-specific properties
  if (isAgentAction(nodeData)) {
    if (nodeData.tools) {
      values['_toolCount'] = Object.keys(nodeData.tools).length;
    }
    
    // Ensure agentModelType has a default value for conditional visibility to work.
    // The manifest has x-ms-input-dependencies that gate the BYOM-only
    // deploymentModelProperties fields (Model name/format/version) on
    // agentModelType === 'AzureOpenAI'. When the workflow just added a
    // Microsoft-managed agent, we may hit this branch before
    // inputs.parameters.agentModelType has propagated. Falling back to
    // 'AzureOpenAI' in that window makes the BYOM deploymentModelProperties
    // group flash into view until the user picks a Microsoft-managed model.
    // Detect Microsoft-managed via the connection ref already extracted into
    // _modelConnection_model1 above and default to the canonical managed id instead.
    if (!values['agentModelType']) {
      const modelConn = values['_modelConnection_model1'];
      const isManaged = typeof modelConn === 'string' && modelConn === MANAGED_MODEL_REFERENCE_KEY;
      values['agentModelType'] = isManaged ? MANAGED_MODEL_PROVIDER_ID : 'AzureOpenAI';
    }

    // Symmetric to extractReferenceKeyFromOperation's Microsoft-managed fallback: when the
    // workflow was authored without a `modelConfigurations.model1.referenceName`
    // (SDK, CLI, or portal saves after we stopped writing the phantom ref) but
    // `agentModelType === 'MicrosoftManaged'`, seed the picker's connection
    // slot with the synthetic Microsoft-managed key so the Connection tab shows
    // "Microsoft-managed" as selected instead of empty.
    if (
      values['agentModelType'] === MANAGED_MODEL_PROVIDER_ID &&
      !values['_modelConnection_model1']
    ) {
      values['_modelConnection_model1'] = MANAGED_MODEL_REFERENCE_KEY;
    }
    
    // Ensure system and user message fields exist (for new Agent actions)
    if (!values['_systemMessage']) {
      values['_systemMessage'] = '';
    }
    if (!values['_userMessage']) {
      values['_userMessage'] = '';
    }
  }

  // Extract operation ID if present
  if ('operationId' in nodeData) {
    const topLevelOpId = (nodeData as Action & { operationId?: string }).operationId ?? null;
    if (!values['_operationId']) {
      values['_operationId'] = topLevelOpId;
    }
  }

  // Extract trigger-specific properties (at root level, not in inputs)
  // These are specific to triggers and need special handling
  const triggerData = nodeData as Record<string, unknown>;

  // Recurrence - polling interval configuration
  if ('recurrence' in triggerData && triggerData.recurrence) {
    const recurrence = triggerData.recurrence as Record<string, unknown>;
    values['_recurrence'] = recurrence;
    // Also extract individual recurrence fields for UI
    if (recurrence.frequency) {
      values['_recurrence.frequency'] = recurrence.frequency as string;
    }
    if (recurrence.interval !== undefined) {
      values['_recurrence.interval'] = recurrence.interval as number;
    }
    if (recurrence.startTime) {
      values['_recurrence.startTime'] = recurrence.startTime as string;
    }
    if (recurrence.timeZone) {
      values['_recurrence.timeZone'] = recurrence.timeZone as string;
    }
  }

  // SplitOn - for splitting trigger outputs into multiple workflow runs
  if ('splitOn' in triggerData && triggerData.splitOn !== undefined) {
    values['_splitOn'] = triggerData.splitOn as string;
  }

  // SplitOnConfiguration - additional splitOn settings
  if ('splitOnConfiguration' in triggerData && triggerData.splitOnConfiguration) {
    values['_splitOnConfiguration'] = triggerData.splitOnConfiguration as Record<string, unknown>;
  }

  // Metadata - operation-specific metadata (e.g., folder paths for blob triggers)
  if ('metadata' in triggerData && triggerData.metadata) {
    values['_metadata'] = triggerData.metadata as Record<string, unknown>;
  }

  // Conditions - trigger conditions
  if ('conditions' in triggerData && Array.isArray(triggerData.conditions)) {
    values['_conditions'] = triggerData.conditions;
  }

  return values;
}

// =============================================================================
// PARAMETER SERIALIZATION (Building inputs for save)
// =============================================================================

/**
 * Build inputs object from parameter values for saving
 * Serializes value segments back to expression strings
 * Unflattens dot-notation parameter keys back to nested structures
 *
 * For ApiConnection actions with swagger metadata, uses swagger-based serialization
 * to properly construct method, path, queries, headers, body structure.
 */
export function buildInputsFromParameters(
  originalInputs: Record<string, unknown>,
  parameterValues: Record<string, ParameterValue>,
  nodeType?: string,
  parameterDefinitions?: ParameterInfo[] | null,
  swaggerMetadata?: SwaggerOperationMetadata
): Record<string, unknown> {
  // For ApiConnectionNotification triggers, rebuild the fetch/subscribe structure
  if (parameterValues['_isNotificationTrigger'] === true) {
    return buildNotificationTriggerInputs(parameterValues, originalInputs);
  }

  // For ApiConnection / ApiConnectionWebhook actions with parameter definitions, use swagger-based serialization
  // This ensures proper structure: method, path (with substitution), queries, headers, body
  // When pathTemplate/method are empty (dynamic schema case), preserve original path/method from inputs
  const hasParameterDefinitions =
    parameterDefinitions !== null && parameterDefinitions !== undefined;

  // ApiConnectionWebhook serializes the same flat shape as ApiConnection
  // (host + path + body). The webhook callbackUrl/notificationUrl is carried
  // through `parameterDefinitions` as a hidden body param with a default of
  // `${listCallbackUrl()}`, set by SwaggerParser when `x-ms-notification-url: true`.
  if (
    (nodeType === 'ApiConnection' ||
      nodeType === 'ApiConnectionWebhook') &&
    hasParameterDefinitions &&
    swaggerMetadata
  ) {
    return buildSwaggerInputsFromDefinitions(
      parameterValues,
      parameterDefinitions,
      swaggerMetadata.method,
      swaggerMetadata.pathTemplate,
      swaggerMetadata.pathEncoding,
      swaggerMetadata.connectionFormat ?? 'ApiConnection',
      swaggerMetadata.connectorId,
      swaggerMetadata.operationId,
      originalInputs  // Pass original inputs to preserve path/method when template is empty
    );
  }

  const inputs = deepClone(originalInputs);
  // ServiceProviderWebhook triggers share the ServiceProvider host shape.
  const isServiceProvider = nodeType === 'ServiceProvider' || nodeType === 'ServiceProviderWebhook';

  // Determine whether this operation's manifest declares that input properties
  // are nested under `inputs.parameters` (e.g. Agent, Service Provider). The
  // runtime requires this wrapper for these operation types, so we honor the
  // manifest declaration rather than rely solely on `_hasParametersWrapper`,
  // which is data-driven and may be `false` for newly created nodes whose
  // initial template did not yet include a `parameters` object.
  const operationManifest = nodeType ? getBuiltInManifest(nodeType) : null;
  const manifestInputSchema = operationManifest?.properties?.inputs as SchemaObject | undefined;
  const manifestInputsLocation = operationManifest?.properties?.inputsLocation;
  const manifestUsesParametersWrapper =
    Array.isArray(manifestInputsLocation) &&
    manifestInputsLocation.length === 2 &&
    manifestInputsLocation[0] === 'inputs' &&
    manifestInputsLocation[1] === 'parameters';

  const hasParametersWrapper =
    parameterValues['_hasParametersWrapper'] === true ||
    isServiceProvider ||
    manifestUsesParametersWrapper;
  const isAgentNode = nodeType === 'Agent';

  // Build a map from parameter name → declared swagger/manifest type so the
  // serializer can apply type-aware coercion (e.g. parse JSON object strings
  // for `type: any`, but preserve literal strings for `type: string`).
  const parameterTypeMap = new Map<string, string>();
  if (parameterDefinitions) {
    for (const def of parameterDefinitions) {
      if (def?.parameterName && def.type) {
        parameterTypeMap.set(def.parameterName, def.type as string);
      }
    }
  }
  const manifestTopLevelProps = manifestInputSchema?.properties;
  if (manifestTopLevelProps) {
    for (const [propName, propSchema] of Object.entries(manifestTopLevelProps)) {
      if (!parameterTypeMap.has(propName) && typeof propSchema?.type === 'string') {
        parameterTypeMap.set(propName, propSchema.type);
      }
    }
  }
  const lookupParamType = (paramKey: string): string | undefined => {
    // Exact match first, then root segment for dot-notation keys
    return parameterTypeMap.get(paramKey) ?? parameterTypeMap.get(paramKey.split('.', 1)[0] ?? paramKey);
  };

  // Honor `x-ms-serialization: { skip: true }` on built-in manifest input properties.
  // These fields (e.g. Request.callbackUrl, Http.inputs.operationId) are display-only
  // or rebuilt from sibling fields and must never be written to the workflow definition.
  // Collect the set of skip keys once, then drop matching keys from both the cloned
  // inputs (in case stale values were present) and the parameter iteration below.
  const skipSerializationKeys = new Set<string>();
  if (manifestTopLevelProps) {
    for (const [propName, propSchema] of Object.entries(manifestTopLevelProps)) {
      const skipFlag = (propSchema as { 'x-ms-serialization'?: { skip?: boolean } })?.[
        'x-ms-serialization'
      ]?.skip;
      if (skipFlag === true) {
        skipSerializationKeys.add(propName);
        if (propName in inputs) {
          delete inputs[propName];
        }
      }
    }
  }

  // Honor `x-ms-input-dependencies` visibility on manifest input properties.
  // When a property's visibility dependencies are not satisfied (e.g. an Agent's
  // `modelId` requires `agentModelType: V1ChatCompletionsService`), the property
  // is hidden in the editor and must also be excluded from serialization. We:
  //   1. Skip writes for these keys during the main parameter loop, and
  //   2. After all writes (including special handlers), strip stale or written
  //      hidden values from the final inputs so switching `agentModelType`
  //      cleanly removes the previous-model fields from the saved JSON.
  //
  // Visibility is evaluated against an effective value map that overlays
  // manifest defaults onto `parameterValues` for missing keys. This ensures
  // that controlling parameters with declared defaults (e.g.
  // `agentHistoryReductionType: 'maximumTokenCountReduction'`) drive
  // visibility correctly even when the user never explicitly set them.
  let hiddenManifestKeys: Set<string> = new Set();
  let manifestDefaults: Record<string, unknown> = {};
  if (manifestInputSchema?.properties) {
    manifestDefaults = collectSchemaDefaults(manifestInputSchema);
    // Overlay defaults onto current values so controlling parameters that the
    // user never explicitly set (e.g. `agentHistoryReductionType`) still drive
    // the visibility evaluation correctly.
    //
    // Also normalize `ValueSegment[]` values to their literal-string form so
    // visibility comparisons against schema-declared string literals match.
    // Without this, a freshly-written controlling parameter (e.g. one set via
    // `x-ms-on-select` that goes through `toLiteralSegments`) is stored as
    // `ValueSegment[]` in `parameterValues`, and `valuesMatch` would compare
    // it as an object vs string and return false — incorrectly marking
    // dependent fields like `deploymentId` as hidden, which then strips them
    // from the serialized workflow.json.
    const effectiveValues: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(parameterValues)) {
      effectiveValues[k] = isValueSegmentArray(v) ? serializeSegmentsToValue(v) : v;
    }
    for (const [k, v] of Object.entries(manifestDefaults)) {
      const existing = effectiveValues[k];
      if (existing === undefined || existing === null || existing === '') {
        effectiveValues[k] = v;
      }
    }
    hiddenManifestKeys = collectHiddenSchemaKeys(manifestInputSchema, effectiveValues, parseInputDependencies);
  }

  if (isServiceProvider) {
    const reservedTopLevel = new Set([
      'serviceProviderConfiguration',
      'parameters',
      'retryPolicy',
      'timeout',
    ]);
    for (const k of Object.keys(inputs)) {
      if (!reservedTopLevel.has(k)) {
        delete inputs[k];
      }
    }
    if (!inputs.parameters || typeof inputs.parameters !== 'object') {
      inputs.parameters = {};
    }
  }

  // Check if original inputs had a body object - if so, body parameters should go there
  const hadBodyObject = typeof originalInputs.body === 'object' &&
                        originalInputs.body !== null &&
                        !Array.isArray(originalInputs.body);

  // Track which top-level keys were part of the original body (for body reconstruction)
  const originalBodyKeys = hadBodyObject
    ? new Set(Object.keys(originalInputs.body as Record<string, unknown>))
    : new Set<string>();

  // Collect model connections for Agent multi-slot serialization
  const modelConnections: Record<string, { referenceName?: string; modelConfiguration?: ModelConfiguration }> = {};
  // Slots switched to the Microsoft-managed model this save — used to
  // scrub any stale BYOM `referenceName` left over in `inputs.modelConfigurations`
  // from before the switch (see write-skip + scrub comments below).
  const slotsSwitchedToManaged = new Set<string>();

  // Agent parameter keys that should go into inputs.parameters (not inputs directly)
  const agentParameterKeys = ['deploymentId', 'agentModelType', 'modelId', 'headers', 'inputFiles'];
  const agentParameters: Record<string, unknown> = {};

  const getTargetObject = (): Record<string, unknown> => {
    if (hasParametersWrapper) {
      if (!inputs.parameters || typeof inputs.parameters !== 'object') {
        inputs.parameters = {};
      }
      return inputs.parameters as Record<string, unknown>;
    }
    return inputs;
  };

  Object.entries(parameterValues).forEach(([key, value]) => {
    // Drop fields marked `x-ms-serialization: { skip: true }` in the manifest.
    // Honors both top-level keys and nested dot-notation keys whose root is a
    // skip-serialized property (e.g. `callbackUrl.foo`).
    if (skipSerializationKeys.size > 0) {
      const rootKey = key.split('.', 1)[0]!;
      if (skipSerializationKeys.has(rootKey)) {
        return;
      }
    }

    // Skip parameters whose visibility dependencies are not satisfied.
    // Internal fields (prefixed with `_`) are processed below by their own
    // logic and are never tied to manifest visibility, so we exclude them.
    if (
      hiddenManifestKeys.size > 0 &&
      !key.startsWith('_') &&
      isPathOrAncestorHidden(key, hiddenManifestKeys)
    ) {
      return;
    }

    if (key.startsWith('_')) {
      // Handle special fields
      if (key === '_connection') {
        // For ApiConnection types, ALWAYS create host block — the runtime needs apiId + operationId
        // to route the call, even before a connection is configured (value may be empty string).
        // For other connection types, only create host when connection is set (value truthy).
        const isApiConnectionType = nodeType === 'ApiConnection' || nodeType === 'ApiConnectionWebhook';
        const isServiceProvider = nodeType === 'ServiceProvider' || nodeType === 'ServiceProviderWebhook';
        if (value || isApiConnectionType) {
          // Resolve apiId/operationId from parameterValues (set during deserialization of host)
          // or fall back to the deepCloned host (from originalInputs) when deserialization
          // didn't find them (e.g., host was present in original but not read into parameterValues).
          const existingHost = inputs.host as Record<string, unknown> | undefined;
          // For ServiceProvider, existing metadata lives in inputs.serviceProviderConfiguration,
          // not inputs.host. Extract serviceProviderId and operationId from there so we don't
          // wipe them when rewriting the serviceProviderConfiguration block.
          const existingSpConfig = inputs.serviceProviderConfiguration as Record<string, unknown> | undefined;
          const apiId = (parameterValues['_apiId'] as string | undefined) ?? (existingHost?.apiId as string | undefined);
          const operationId = (parameterValues['_operationId'] as string | undefined)
            ?? (existingHost?.operationId as string | undefined)
            ?? (isServiceProvider ? (existingSpConfig?.operationId as string | undefined) : undefined);
          const serviceProviderId = isServiceProvider
            ? (existingSpConfig?.serviceProviderId as string | undefined)
            : undefined;

          const serialized = serializeHost({
            connectionType: isServiceProvider ? 'ServiceProvider' : (nodeType ?? 'ApiConnection'),
            connectionKey: (value as string) || '',
            ...(apiId ? { apiId } : {}),
            ...(operationId ? { operationId } : {}),
            ...(serviceProviderId ? { serviceProviderId } : {}),
          });
          applyHostSerialization(inputs, serialized);
        }
      } else if (key.startsWith('_modelConnection_') && value) {
        const slotId = key.replace('_modelConnection_', '');
        // Microsoft-managed refs are portal-only scaffolding — the runtime
        // dispatches on a managed-provider alias in `parameters.agentModelType` and
        // ignores `modelConfigurations.model1.referenceName`. Omitting the write
        // keeps workflow.json clean for SDK/CLI/manual authors (no need to emit a
        // phantom `managedAiConnection` ref); the load-time fallback in
        // extractReferenceKeyFromOperation rehydrates it from agentModelType.
        if (value !== MANAGED_MODEL_REFERENCE_KEY) {
          if (!modelConnections[slotId]) modelConnections[slotId] = {};
          modelConnections[slotId].referenceName = value as string;
        } else {
          // The user just switched this slot to Microsoft-managed. Track it so
          // we can scrub any stale BYOM referenceName already persisted in
          // inputs.modelConfigurations[slotId] below — otherwise a workflow
          // authored with BYOM (e.g. by Copilot) then switched to managed keeps
          // pointing at the old connection reference, which later shows as
          // "connection reference not found" once that connection is pruned.
          slotsSwitchedToManaged.add(slotId);
        }
      } else if (key.startsWith('_modelConfig_') && value) {
        const slotId = key.replace('_modelConfig_', '');
        if (!modelConnections[slotId]) modelConnections[slotId] = {};
        modelConnections[slotId].modelConfiguration = value as ModelConfiguration;
      } else if (key === '_codeInterpreterEnabled') {
        // Handle code interpreter toggle - will be processed separately below
        // Skip here, handled after the loop
      } else if (key === '_workflowId' && value) {
        // Handle workflow reference update (for sub-workflow / handoff tools)
        applyHostSerialization(inputs, serializeHost({
          connectionType: 'Workflow',
          connectionKey: value as string,
        }), { merge: true });
      }
      // Skip other internal fields:
      // - Swagger: _isSwaggerOperation, _method, _path, _pathTemplate, _swaggerPathTemplate,
      //            _pathParamNames, _queryParamNames, _headerParamNames
      // - Trigger: _recurrence, _recurrence.*, _splitOn, _splitOnConfiguration, _metadata, _conditions
      // - Common: _apiId, _operationId, _toolCount, _systemMessage, _userMessage, _hasParametersWrapper
    } else if (
      key === 'agentModelSettings.agentChatCompletionSettings.builtinTools' ||
      key.startsWith('agentModelSettings.agentHarness.')
    ) {
      // Handled by dedicated post-loop sections:
      //   - builtinTools → via _codeInterpreterEnabled
      //   - agentHarness.* → via agent harness handler below
      // All other agentModelSettings.* keys (deploymentModelProperties.*,
      // agentChatCompletionSettings.{temperature, topP, maxTokens, ...},
      // agentHistoryReductionSettings.*) flow through the dot-notation
      // handler below so user-typed and user-cleared values are persisted.
    } else if (key === 'inputs') {
      // Skip 'inputs' key - this is the JSON blob representation of the entire inputs object
      // stored for display purposes. The actual input properties are extracted/serialized individually.
      // For Compose-type actions, inputs is handled specially in nodeEditorStore.saveNode()
    } else if (key === 'modelConfiguration') {
      inputs[key] = value;
    } else if (isAgentNode && agentParameterKeys.includes(key)) {
      // For Agent actions, these parameters go into inputs.parameters
      const paramType = lookupParamType(key);
      if (isValueSegmentArray(value)) {
        agentParameters[key] = serializeParameterValue(serializeSegmentsToValue(value), paramType);
      } else if (value !== null && value !== undefined) {
        agentParameters[key] = serializeParameterValue(value, paramType);
      }
    } else if (key.includes('.')) {
      // Handle dot-notation keys - unflatten back to nested structure
      const paramType = lookupParamType(key);
      let serializedValue: unknown;
      if (isValueSegmentArray(value)) {
        serializedValue = serializeParameterValue(serializeSegmentsToValue(value), paramType);
      } else if (value !== null) {
        serializedValue = serializeParameterValue(value, paramType);
      } else {
        serializedValue = value;
      }

      // Check if this key belongs to the body (e.g., 'recipient.groupId' -> body.recipient.groupId)
      const topLevelKey = key.split('.')[0]!;
      if (hadBodyObject && originalBodyKeys.has(topLevelKey)) {
        // This parameter was from the body - put it back in inputs.body
        if (!inputs.body || typeof inputs.body !== 'object') {
          inputs.body = {};
        }
        setNestedValue(inputs.body as Record<string, unknown>, key, serializedValue);
      } else {
        const target = getTargetObject();
        setNestedValue(target, key, serializedValue);
      }
    } else if (hadBodyObject && originalBodyKeys.has(key)) {
      // Non-nested body parameter (e.g., 'messageBody')
      const paramType = lookupParamType(key);
      let serializedValue: unknown;
      if (isValueSegmentArray(value)) {
        serializedValue = serializeParameterValue(serializeSegmentsToValue(value), paramType);
      } else if (value !== null) {
        serializedValue = serializeParameterValue(value, paramType);
      } else {
        serializedValue = value;
      }
      if (!inputs.body || typeof inputs.body !== 'object') {
        inputs.body = {};
      }
      (inputs.body as Record<string, unknown>)[key] = serializedValue;
    } else if (isValueSegmentArray(value)) {
      const paramType = lookupParamType(key);
      const target = getTargetObject();
      target[key] = serializeParameterValue(serializeSegmentsToValue(value), paramType);
    } else if (value !== null) {
      const paramType = lookupParamType(key);
      const target = getTargetObject();
      target[key] = serializeParameterValue(value, paramType);
    }
  });

  // Serialize Agent multi-slot model connections
  if (Object.keys(modelConnections).length > 0) {
    const existingModelConfigs = (inputs.modelConfigurations as Record<string, unknown>) ?? {};
    inputs.modelConfigurations = {
      ...existingModelConfigs,
      ...Object.fromEntries(
        Object.entries(modelConnections).map(([slotId, config]) => [
          slotId,
          {
            ...(existingModelConfigs[slotId] as Record<string, unknown> ?? {}),
            ...(config.referenceName && { referenceName: config.referenceName }),
            ...(config.modelConfiguration && { modelConfiguration: config.modelConfiguration }),
          },
        ])
      ),
    };
  }

  // Remove modelConfigurations entries for slots using Microsoft-managed models.
  // Microsoft-managed dispatch is fully described by agentModelType + deploymentId;
  // neither a connection reference nor a BYOM modelConfiguration should persist.
  // Two cases land here:
  //  1. Legacy on-disk workflows where the portal itself once wrote the
  //     sentinel `managedAiConnection` key as referenceName.
  //  2. A slot switched from a real BYOM connection to managed this save —
  //     without this scrub the old BYOM slot metadata would silently survive.
  if (inputs.modelConfigurations && typeof inputs.modelConfigurations === 'object') {
    const modelConfigs = inputs.modelConfigurations as Record<string, Record<string, unknown>>;
    for (const [slotId, slot] of Object.entries(modelConfigs)) {
      if (!slot) continue;
      const isStaleManagedSentinel = slot.referenceName === MANAGED_MODEL_REFERENCE_KEY;
      const isSwitchedToManagedThisSave = slotsSwitchedToManaged.has(slotId);
      if (isStaleManagedSentinel || isSwitchedToManagedThisSave) {
        delete modelConfigs[slotId];
      }
    }
    if (Object.keys(modelConfigs).length === 0) {
      delete inputs.modelConfigurations;
    }
  }

  // Handle code interpreter toggle for Agent actions
  // This updates inputs.parameters.agentModelSettings.agentChatCompletionSettings.builtInTools
  // when the UI toggle value is present in parameterValues.
  if ('_codeInterpreterEnabled' in parameterValues) {
    const codeInterpreterEnabled = parameterValues['_codeInterpreterEnabled'] as boolean;
    
    // Get or create the nested structure, making copies to avoid mutating originals
    const existingParams = (inputs.parameters ?? {}) as Record<string, unknown>;
    const existingAgentModelSettings = (existingParams.agentModelSettings ?? {}) as Record<string, unknown>;
    const existingChatSettings = (existingAgentModelSettings.agentChatCompletionSettings ?? {}) as Record<string, unknown>;
    
    // Create new objects with the updated values
    const newChatSettings = { ...existingChatSettings };
    
    if (codeInterpreterEnabled) {
      // Add code_interpreter to builtInTools
      newChatSettings.builtInTools = ['code_interpreter'];
    } else {
      // Remove builtInTools
      delete newChatSettings.builtInTools;
    }
    
    // Build the structure back up, only including non-empty objects
    const newAgentModelSettings = { ...existingAgentModelSettings };
    if (Object.keys(newChatSettings).length > 0) {
      newAgentModelSettings.agentChatCompletionSettings = newChatSettings;
    } else {
      delete newAgentModelSettings.agentChatCompletionSettings;
    }
    
    const newParams = { ...existingParams };
    if (Object.keys(newAgentModelSettings).length > 0) {
      newParams.agentModelSettings = newAgentModelSettings;
    } else {
      delete newParams.agentModelSettings;
    }
    
    inputs.parameters = newParams;
  }

  // Handle system and user messages for Agent actions.
  // These need to be combined into inputs.parameters.messages array.
  //
  // Backend rules (see EdgeFlowDefinitionValidationEngine.cs):
  //  - FoundryAgentServiceV2: messages must contain ONLY User entries.
  //  - All other agent model types: messages must contain at least one System entry.
  //  - All messages must have non-empty content.
  //
  // We only emit messages whose content is non-empty so the backend's
  // content-empty check never fires on auto-filled placeholders. If the
  // resulting array would be empty, fall back to a single role-appropriate
  // entry so the missing-property check fires instead.
  if ('_systemMessage' in parameterValues || '_userMessage' in parameterValues) {
    const rawSystemMessage = parameterValues['_systemMessage'];
    const rawUserMessage = parameterValues['_userMessage'];
    const rawAgentModelType = parameterValues['agentModelType'];

    const systemMessage = isValueSegmentArray(rawSystemMessage)
      ? serializeSegmentsToValue(rawSystemMessage) as string
      : (rawSystemMessage as string | undefined);
    const userMessage = isValueSegmentArray(rawUserMessage)
      ? serializeSegmentsToValue(rawUserMessage) as string
      : (rawUserMessage as string | undefined);
    const agentModelType = isValueSegmentArray(rawAgentModelType)
      ? serializeSegmentsToValue(rawAgentModelType) as string
      : (rawAgentModelType as string | undefined);

    const isV2 = agentModelType === 'FoundryAgentServiceV2';
    const isHostedAgent = agentModelType === 'FoundryHostedAgent';
    const messages: Array<{ role: string; content: string }> = [];

    // Non-V2, non-hosted agents get a default System message injected as a product/runtime
    // baseline so the agent always has instructions when the author hasn't provided one. This is a
    // normalization choice, NOT a hard validator rule — EdgeFlowDefinitionValidationEngine only
    // requires a non-empty messages array with valid roles/content, not a System entry.
    if (!isV2 && !isHostedAgent) {
      const trimmedSystem = typeof systemMessage === 'string' ? systemMessage.trim() : '';
      messages.push({
        role: 'System',
        content: trimmedSystem || DEFAULT_AGENT_SYSTEM_MESSAGE,
      });
    } else if (isHostedAgent) {
      // FoundryHostedAgent: include an author-provided System message (instructions) ONLY when
      // non-empty. Do NOT inject DEFAULT_AGENT_SYSTEM_MESSAGE — when absent, the hosted-agent
      // shim applies its own default prompt (and the runtime only forwards a non-empty
      // system_prompt). This keeps existing User-only hosted agents unchanged.
      const trimmedSystem = typeof systemMessage === 'string' ? systemMessage.trim() : '';
      if (trimmedSystem) {
        messages.push({ role: 'System', content: trimmedSystem });
      }
    }
    if (typeof userMessage === 'string' && userMessage.trim()) {
      messages.push({ role: 'User', content: userMessage });
    }

    if (messages.length === 0) {
      // V2 with no user input — emit an empty User entry so the backend reports
      // the actionable missing-content error rather than missing-property.
      messages.push({ role: 'User', content: '' });
    }

    // Get or create parameters object
    const existingParams = (inputs.parameters ?? {}) as Record<string, unknown>;
    const newParams = { ...existingParams };
    newParams.messages = messages;

    inputs.parameters = newParams;
  }

  // Merge collected Agent parameters into inputs.parameters
  if (isAgentNode) {
    const existingParams = (inputs.parameters ?? {}) as Record<string, unknown>;

    // `deploymentId`/`modelId` are the model-identity fields tied to the
    // agent's slot connection. `setSlotConnection` deletes both from
    // `parameterValues` whenever the connection changes (see
    // nodeEditorStore.ts) — including switches to Microsoft-managed, where
    // `deploymentId` stays *visible* (it's repurposed to hold the curated
    // managed model id) but its old BYOM value is no longer valid. Because
    // the main parameter loop above only ever *writes* keys present in
    // `parameterValues`, a deleted key was previously left untouched here,
    // so the stale value from `existingParams` (cloned from the
    // pre-switch workflow.json) silently survived every save. Absence in
    // `parameterValues` means "this save intentionally cleared the field" —
    // carry that deletion through to the merged output.
    for (const modelIdentityKey of ['deploymentId', 'modelId']) {
      if (!(modelIdentityKey in parameterValues) && modelIdentityKey in existingParams) {
        delete existingParams[modelIdentityKey];
      }
    }

    // Same bug class, different fields: `temperature`/`topP` have no
    // `x-ms-input-dependencies` visibility rule in the manifest (unlike
    // `frequencyPenalty`/`presencePenalty`, which are AzureOpenAI-only), so
    // they stay visible across every `agentModelType`. `setSlotConnection`
    // deletes both dot-notation keys from `parameterValues` whenever the
    // model1 connection changes, since a value tuned for one model (e.g.
    // `temperature: 0.2` set by the Workflow Assistant for a BYOM GPT model)
    // can be rejected outright by another (Microsoft-managed models are
    // often reasoning models that only accept the default `temperature: 1`).
    // Without this scrub, the stale value cloned from the pre-switch
    // workflow.json would silently survive every save and the workflow
    // would keep failing at runtime with an `unsupported_value` error from
    // the model provider. Absence in `parameterValues` means "reset to the
    // new model's default" — carry that deletion through, same as above.
    const agentModelSettings = existingParams.agentModelSettings as Record<string, unknown> | undefined;
    if (agentModelSettings && typeof agentModelSettings === 'object') {
      const chatSettings = agentModelSettings.agentChatCompletionSettings as Record<string, unknown> | undefined;
      if (chatSettings && typeof chatSettings === 'object') {
        const mutableChatSettings = Object.isFrozen(chatSettings) ? { ...chatSettings } : chatSettings;
        for (const settingKey of ['temperature', 'topP']) {
          const flatKey = `agentModelSettings.agentChatCompletionSettings.${settingKey}`;
          if (!(flatKey in parameterValues) && settingKey in mutableChatSettings) {
            delete mutableChatSettings[settingKey];
          }
        }
        if (mutableChatSettings !== chatSettings) {
          agentModelSettings.agentChatCompletionSettings = mutableChatSettings;
        }
        if (Object.keys(mutableChatSettings).length === 0) {
          delete agentModelSettings.agentChatCompletionSettings;
        }
      }
      if (Object.keys(agentModelSettings).length === 0) {
        delete existingParams.agentModelSettings;
      }
    }

    if (Object.keys(agentParameters).length > 0) {
      inputs.parameters = {
        ...existingParams,
        ...agentParameters,
      };
    } else {
      inputs.parameters = existingParams;
    }
  }

  // For FoundryAgentService, ensure foundryAgentId/foundryAgentName/agentModelType
  // are placed correctly: foundryAgentId at top-level only, foundryAgentName and
  // agentModelType in both top-level inputs and inputs.parameters.
  // Properties may land at either level depending on whether _hasParametersWrapper
  // was set (existing workflow) or not (new node), so check both locations.
  if (isAgentNode) {
    const params = (inputs.parameters ?? {}) as Record<string, unknown>;
    const rootModelType = inputs.agentModelType as string | undefined;
    // The editor value is authoritative when the user switches Foundry API
    // versions. Falling back to the original root value here would silently
    // restore the previously saved version during serialization.
    const modelType = (
      agentParameters.agentModelType ??
      params.agentModelType ??
      rootModelType
    ) as string | undefined;
    if (modelType === 'FoundryAgentService' || modelType === 'FoundryAgentServiceV2') {
      // foundryAgentId goes to top-level only (not in parameters)
      const agentId = params.foundryAgentId ?? inputs.foundryAgentId;
      if (agentId !== undefined) {
        inputs.foundryAgentId = agentId;
      }
      delete params.foundryAgentId;

      // foundryAgentName goes to both top-level and parameters
      const agentName = params.foundryAgentName ?? inputs.foundryAgentName;
      if (agentName !== undefined) {
        inputs.foundryAgentName = agentName;
        params.foundryAgentName = agentName;
      }

      // foundryAgentVersion: v1 agents include it in both top-level and parameters;
      // v2 agents omit it entirely
      if (modelType === 'FoundryAgentService') {
        inputs.foundryAgentVersion = 'v1';
        params.foundryAgentVersion = 'v1';
        delete inputs.foundryVersionName;
        delete params.foundryVersionName;
      } else {
        delete inputs.foundryAgentVersion;
        delete params.foundryAgentVersion;
      }

      // V2 runtime deserialization reads parameters.agentModelType, while the
      // root value identifies the designer variant. Keep V2 in both locations;
      // legacy V1 continues to use only the root value.
      inputs.agentModelType = modelType;
      if (modelType === 'FoundryAgentService') {
        delete params.agentModelType;
      } else {
        params.agentModelType = modelType;
      }

      inputs.parameters = params;
    } else if (modelType === 'FoundryHostedAgent') {
      // FoundryHostedAgent: ensure agentModelType is always at root level so the
      // runtime's EffectiveAgentModelType resolves correctly during validation.
      inputs.agentModelType = modelType;
      inputs.parameters = params;
    }
  }

  // For AzureOpenAI Agent actions, ensure deploymentModelProperties is set in agentModelSettings
  if (hasParametersWrapper) {
    const params = (inputs.parameters ?? {}) as Record<string, unknown>;
    const agentModelType = (params.agentModelType ?? inputs.agentModelType) as string | undefined;
    
    if (agentModelType === 'AzureOpenAI') {
      const existingAgentModelSettings = (params.agentModelSettings ?? {}) as Record<string, unknown>;
      const existingDeploymentModelProps = (existingAgentModelSettings.deploymentModelProperties ?? {}) as Record<string, unknown>;
      
      // Only seed `name` when truly missing (undefined). Empty string means
      // the user explicitly cleared the field and must be preserved.
      if (existingDeploymentModelProps.name === undefined) {
        // Use deploymentId as the model name if available, otherwise use a default
        const deploymentId = params.deploymentId as string | undefined;
        const modelName = deploymentId || 'gpt-4.1';
        
        const newDeploymentModelProps = {
          ...existingDeploymentModelProps,
          name: modelName,
        };
        
        const newAgentModelSettings = {
          ...existingAgentModelSettings,
          deploymentModelProperties: newDeploymentModelProps,
        };
        
        inputs.parameters = {
          ...params,
          agentModelSettings: newAgentModelSettings,
        };
      }
    }
  }

  // Handle agent harness settings for Agent actions
  // These are stored as flattened keys (agentModelSettings.agentHarness.*)
  // and need to be written back to the nested structure in inputs.parameters
  const harnessPrefix = 'agentModelSettings.agentHarness.';
  const harnessEntries = Object.entries(parameterValues).filter(([k]) => k.startsWith(harnessPrefix));
  if (harnessEntries.length > 0) {
    // Build a fresh agentHarness object from current parameter values only
    // (don't merge with originalInputs to avoid preserving stale/cleared values)
    const agentHarness: Record<string, unknown> = {};
    for (const [key, value] of harnessEntries) {
      const harnessKey = key.substring(harnessPrefix.length);
      // Skip null/undefined values and empty arrays
      if (value === null || value === undefined) continue;
      if (Array.isArray(value) && value.length === 0) continue;
      agentHarness[harnessKey] = value;
    }

    // Include agentHarness only if a harness type is set (ADC sandbox scenario).
    // FoundryHostedAgent stores inputFiles at parameters.inputFiles level instead.
    const shouldIncludeHarness = !!agentHarness.type;

    const existingParams = (inputs.parameters ?? {}) as Record<string, unknown>;
    const existingAgentModelSettings = (existingParams.agentModelSettings ?? {}) as Record<string, unknown>;
    const newAgentModelSettings = { ...existingAgentModelSettings };

    if (shouldIncludeHarness) {
      newAgentModelSettings.agentHarness = agentHarness;
    } else {
      delete newAgentModelSettings.agentHarness;
    }

    const newParams = { ...existingParams };
    if (Object.keys(newAgentModelSettings).length > 0) {
      newParams.agentModelSettings = newAgentModelSettings;
    } else {
      delete newParams.agentModelSettings;
    }

    inputs.parameters = newParams;
  }

  // Handle swagger-based operation serialization
  // Use the stored metadata to rebuild path/queries/headers from parameter values
  if (parameterValues['_isSwaggerOperation']) {
    const swaggerInputs = serializeSwaggerParameters(parameterValues);
    if (swaggerInputs) {
      // Merge swagger-rebuilt inputs (path, queries, headers, body)
      // while preserving other inputs (host, authentication, etc.)
      Object.assign(inputs, swaggerInputs);
    }
  }

  // Inject manifest defaults for visible properties that are still missing
  // after special handlers have run. The runtime requires certain fields to
  // be present even when the user never explicitly edited them (e.g. for
  // AzureOpenAI agents: `agentModelSettings.deploymentModelProperties.{name,
  // format, version}`). Defaults are written via `setNestedDefault` which
  // creates intermediate objects and only fills missing leaves, so previously
  // configured values are preserved.
  //
  // For operations whose manifest declares `inputsLocation: ['inputs',
  // 'parameters']` (Agent, ServiceProvider), defaults always target
  // `inputs.parameters` so the runtime sees them at the expected path. We
  // also relocate any stray manifest-declared properties that ended up at
  // the top of `inputs` (e.g. from a previous bad save) so the cleanup
  // pass finds and processes them in the correct location.
  if (manifestUsesParametersWrapper && manifestTopLevelProps) {
    if (!isObject(inputs.parameters)) {
      inputs.parameters = {};
    }
    const params = inputs.parameters as Record<string, unknown>;
    for (const propName of Object.keys(manifestTopLevelProps)) {
      if (propName.startsWith('_')) continue;
      if (FOUNDRY_AGENT_TOP_LEVEL_KEYS.has(propName)) continue;
      if (propName in inputs && !(propName in params)) {
        // Move the stray top-level value into the parameters wrapper.
        params[propName] = inputs[propName];
      }
      if (propName in inputs && propName !== 'parameters') {
        delete inputs[propName];
      }
    }
  }

  if (Object.keys(manifestDefaults).length > 0) {
    let defaultTarget: Record<string, unknown>;
    if (hasParametersWrapper) {
      if (!isObject(inputs.parameters)) {
        inputs.parameters = {};
      }
      defaultTarget = inputs.parameters as Record<string, unknown>;
    } else {
      defaultTarget = inputs;
    }
    for (const [flatKey, defaultValue] of Object.entries(manifestDefaults)) {
      if (isPathOrAncestorHidden(flatKey, hiddenManifestKeys)) continue;
      setNestedDefault(defaultTarget, flatKey, defaultValue);
    }
  }

  // Final visibility cleanup: strip any properties whose `x-ms-input-dependencies`
  // visibility rules are not satisfied. This catches both stale values cloned
  // from `originalInputs` and values written by special handlers above (e.g.
  // the `_codeInterpreterEnabled` toggle, AzureOpenAI deploymentModelProperties
  // defaults). For nodes whose inputs are wrapped under `inputs.parameters`
  // (Agent, ServiceProvider), the keys live under that wrapper; otherwise they
  // live directly under `inputs`.
  if (hiddenManifestKeys.size > 0) {
    const wrapperTarget = hasParametersWrapper
      ? (inputs.parameters as Record<string, unknown> | undefined)
      : inputs;
    if (wrapperTarget && typeof wrapperTarget === 'object') {
      for (const hiddenKey of hiddenManifestKeys) {
        deleteNestedKey(wrapperTarget, hiddenKey);
      }
    }
  }

  // Final cleanup: for Foundry V1 / FoundryHostedAgent nodes, ensure agentModelType
  // lives ONLY at the top level and not also in inputs.parameters (the manifest
  // defaults pass above may have re-injected it). V2 keeps it in both levels.
  if (isAgentNode && inputs.parameters && typeof inputs.parameters === 'object') {
    const params = inputs.parameters as Record<string, unknown>;
    const rootModelType = inputs.agentModelType as string | undefined;
    if (rootModelType === 'FoundryAgentService' || rootModelType === 'FoundryHostedAgent') {
      delete params.agentModelType;
    }
    // For FoundryHostedAgent, also clean up internal keys that shouldn't be serialized
    if (rootModelType === 'FoundryHostedAgent') {
      delete params.hostedAgentName;
      delete params.deploymentId;

      // Filter out empty inputFiles entries (name is required). Content may be a string OR a
      // Logic Apps content-envelope object ({ $content-type, $content }) — the runtime accepts both
      // (FoundryHostedAgentInputFile.Content is a JToken, consumed via AgentInputFile.FromRawContent).
      // Only those two shapes are kept: any other object would be stringified by the runtime (or, for
      // multipart/formdata-shaped envelopes, throw later), so drop it here so invalid shapes fail early.
      // The optional `isSkill` flag rides through verbatim (filter keeps whole entries) so the runtime
      // can stage the file as a Copilot skill — see FoundryHostedAgentInputFile.IsSkill.
      if (Array.isArray(params.inputFiles)) {
        const validFiles = (params.inputFiles as Array<{ name?: unknown; content?: unknown; isSkill?: unknown }>)
          .filter((f): f is { name: string; content: string | { '$content-type': string; '$content': string }; isSkill?: boolean } => {
            if (!f || typeof f.name !== 'string' || f.name.trim().length === 0) {
              return false;
            }
            if (typeof f.content === 'string') {
              return true;
            }
            if (typeof f.content === 'object' && f.content !== null && !Array.isArray(f.content)) {
              const envelope = f.content as Record<string, unknown>;
              return typeof envelope['$content-type'] === 'string' && typeof envelope['$content'] === 'string';
            }
            return false;
          });
        if (validFiles.length > 0) {
          params.inputFiles = validFiles;
        } else {
          delete params.inputFiles;
        }
      }
    }
  }

  return inputs;
}
